<script>
import Layout from '@layouts/main'
import api from '@utils/api'
import formatDate from '@utils/format-date'
import UserDelete from './user-delete'
import UserNew from './user-new'
import Filters from '@views/filters-dialog'
import userModel from '@src/models/user'
import TablePaginator from '@components/paginator'
import { hideValues, updateColumns } from '@utils/helpers/columns'
import AdjustColumnsDialog from '@components/adjust-columns-dialog'

export default {
  name: 'Users',
  components: {
    Layout,
    UserDelete,
    UserNew,
    Filters,
    TablePaginator,
    AdjustColumnsDialog,
  },
  data() {
    return {
      modelName: 'users',
      isLoading: false,
      headers: [
        { text: 'user_name', value: 'login', sortable: true },
        { text: 'email', value: 'email', sortable: true, show: true },
        { text: 'last_name', value: 'lastName', sortable: true, show: true },
        { text: 'first_name', value: 'firstName', sortable: true, show: true },
        { text: 'phone', value: 'tel', sortable: true, show: true },
        {
          text: 'description',
          value: 'description',
          sortable: true,
          show: true,
        },
        { text: 'status', value: 'status', sortable: true, show: true },
        { text: 'created', value: 'createTime', sortable: true, show: true },
        { text: 'actions', value: 'actions', sortable: false },
      ],
      rowsPerPage: [10, 25, 50],
      page: 1,
      totalPages: 0,
      sortBy: 'login',
      descending: false,
      first: false,
      last: false,
      content: [],
      pagination: {},
      newUser: false,
      showFilters: false,
      model: userModel,
      filters: null,
      fab: false,
      showDialog: false,
    }
  },
  computed: {
    params() {
      return {
        size: this.pagination.rowsPerPage || null,
        page: this.page || null,
        sort_property: this.sortBy || null,
        sort_direction: this.descending || null,
        ...this.filters,
      }
    },
    userId() {
      return this.$store.state.auth.user.sub
    },
  },
  watch: {
    pagination: {
      handler() {
        this.$store.commit('pagination/SET', {
          name: this.modelName,
          pagination: {
            page: this.page,
            rowsPerPage: this.pagination.rowsPerPage,
            sortBy: this.sortBy,
            descending: this.descending,
          },
        })
      },
      deep: true,
    },
  },
  created() {
    this.getPaginator()
    this.getFilters()
  },
  methods: {
    fetchData() {
      this.isLoading = true
      api.getUsers(this.params).then(response => {
        this.content = response.content.filter(
          elem => elem.lastName !== 'Service'
        )
        this.totalPages = response.totalPages
        this.first = response.first
        this.last = response.last
        this.page = response.number + 1
        this.pagination = {
          ...this.pagination,
          page: 1,
          rowsPerPage: response.size,
          sortBy: this.sortBy,
          descending: this.descending,
        }
        this.isLoading = false
      })
    },
    getValue(dictionaryName, value) {
      return this.$store.getters['data/getValueFromDictionary'](
        dictionaryName,
        value
      )
    },
    dateFormat(date) {
      return formatDate(date)
    },
    openUser(id) {
      this.$router.push(`users/${id}`)
    },
    getFilters() {
      const filter = this.$store.getters['filters/getFilters'](this.modelName)
      this.filters = filter ? filter.value : null
      this.fetchData()
    },
    setFilters() {
      const filter = this.$store.getters['filters/getFilters'](this.modelName)
      this.filters = filter ? filter.value : null
      this.showFilters = false
      this.page = 1
      this.fetchData()
    },
    getPaginator() {
      const pagination = this.$store.getters['pagination/get'](this.modelName)
      if (pagination) {
        this.page = pagination.page || 1
        this.sortBy = pagination.sortBy || this.sortBy
        this.descending = pagination.descending || this.descending
        this.pagination.page = 1
        this.pagination.rowsPerPage = pagination.rowsPerPage || 10
      } else {
        this.page = 1
        this.pagination.page = 1
        this.pagination.rowsPerPage = 10
      }
    },
    changePage(value) {
      this.page = value
      this.fetchData()
    },
    changeRowsPerPage(value) {
      if (this.pagination.rowsPerPage === value) return
      this.pagination = {
        ...this.pagination,
        rowsPerPage: value,
      }
      this.page = 1
      this.$store.dispatch('pagination/saveRowsPerPage', value)
      this.fetchData()
    },
    changeSort(header) {
      if (!header.sortable) return
      if (this.sortBy === header.value) {
        this.descending = !this.descending
      } else {
        this.sortBy = header.value
        this.descending = false
      }
      this.fetchData()
    },
    getSortClass(header) {
      return [
        header.sortable ? 'column sortable' : null,
        this.pagination.descending ? 'desc' : 'asc',
        header.value === this.pagination.sortBy ? 'active' : '',
      ]
    },
    hideValues(field) {
      return hideValues(field, this.headers)
    },
    updateColumns(columns) {
      updateColumns(columns, this.headers)
    },
    openDialog() {
      this.showDialog = true
    },
  },
}
</script>

<template>
  <Layout>
    <div slot="actionButton">
      <div class="hidden-xs-only">
        <v-btn
          color="info"
          @click="fetchData"
        >
          <v-icon>refresh</v-icon>
        </v-btn>
        <v-btn
          :color="filters ? `success` : `info`"
          @click="showFilters = true"
        >
          <v-icon>filter_list</v-icon>
        </v-btn>
        <v-btn
          slot="activator"
          color="primary"
          dark
          @click="newUser = true"
        >
          <v-icon>add</v-icon>
        </v-btn>
      </div>
      <div class="hidden-sm-and-up">
        <v-speed-dial
          v-model="fab"
          direction="left"
          transition="slide-y-reverse-transition"
        >
          <v-btn
            slot="activator"
            v-model="fab"
            color="blue darken-2"
            dark
            fab
            small
            flat
          >
            <v-icon>more_vert</v-icon>
            <v-icon>close</v-icon>
          </v-btn>
          <v-btn
            color="primary"
            dark
            small
            fab
            @click="newUser = true"
          >
            <v-icon>add</v-icon>
          </v-btn>
          <v-btn
            :color="filters ? `success` : `info`"
            small
            fab
            @click="showFilters = true"
          >
            <v-icon>filter_list</v-icon>
          </v-btn>
          <v-btn
            color="info"
            small
            fab
            @click="fetchData"
          >
            <v-icon>refresh</v-icon>
          </v-btn>
        </v-speed-dial>
      </div>
    </div>
    <v-layout justify-end>
      <v-btn
        icon
        flat
        @click="openDialog()">
        <v-icon>view_column</v-icon>
      </v-btn>
    </v-layout>
    <v-data-table
      :headers="headers"
      :items="content"
      :pagination="pagination"
      :loading="isLoading"
      :class="$style.sdTable"
      hide-actions
    >
      <template 
        slot="headers" 
        slot-scope="props">
        <tr>
          <th
            v-for="header in props.headers"
            :key="header.text"
            :class="getSortClass(header)"
            :style="[header.show === false ? {'display': 'none'} : {'text-align': 'left'}]"
            @click="changeSort(header)"
          >
            {{ $t(`label__${header.text}`) }}
            <v-icon 
              v-if="header.sortable" 
              small>expand_less</v-icon>
          </th>
        </tr>
      </template>
      <v-progress-linear
        slot="progress"
        height="2"
        color="blue"
        background-color="#E0E0E0"
        indeterminate
      />
      <template
        slot="items"
        slot-scope="props"
      >
        <tr
          :class="$style.sdEditRow"
          @click="openUser(props.item.id)">
          <td>{{ props.item.login }}</td>
          <td v-if="!hideValues('email')">{{ props.item.email }}</td>
          <td v-if="!hideValues('lastName')">{{ props.item.lastName }}</td>
          <td v-if="!hideValues('firstName')">{{ props.item.firstName }}</td>
          <td v-if="!hideValues('tel')">{{ props.item.tel }}</td>
          <td v-if="!hideValues('description')">{{ props.item.description }}</td>
          <td v-if="!hideValues('status')">{{ $t(`userstatus.${props.item.status}.name`) }}</td>
          <td v-if="!hideValues('createTime')">{{ dateFormat(props.item.createTime) }}</td>
          <td>
            <user-delete
              v-if="Number(userId) !== props.item.id"
              :username="props.item.login"
              :user-id="props.item.id"
              @user-deleted="fetchData(params)"
            />
          </td>
        </tr>
      </template>
    </v-data-table>
    <table-paginator
      :page="page"
      :total-pages="totalPages"
      :first="first"
      :last="last"
      :rows-per-page="pagination.rowsPerPage"
      @change-page="changePage($event)"
      @change-rows-per-page="changeRowsPerPage($event)" />
    <adjust-columns-dialog
      v-model="showDialog"
      :headers="headers.filter(h => h.show !== undefined)"
      :model-name="modelName"
      @columns-updated="updateColumns"
    />
    <user-new
      :new-user="newUser"
      @close-modal="newUser = false" />
    <filters
      :visible="showFilters"
      :model="model"
      :model-name="modelName"
      @close-modal="showFilters = false"
      @filters-confirmed="setFilters()"
    />
  </Layout>
</template>

<style lang="scss" module>
@import '@design';
.sdTable {
  border: 1px solid #dfdfdf;

  th {
    outline: none;
  }
}
.sdEditRow {
  cursor: pointer;
}
.sdBorder {
  border: 1px solid #dfdfdf;
  border-bottom: none;
}
</style>
